import { AlertSeverity } from "data/models";
import { CardElement } from "@stripe/react-stripe-js";
import { Stripe, StripeCardElement, StripeElements } from "@stripe/stripe-js";
import { DefaultError } from "@tanstack/react-query";
import { Auth } from "aws-amplify";
import { InvitationItem } from "pages/geoscape-hub/settings/organisation/members/invite-members";
import {
  BuildingsSampleRequest,
  Coupon,
  CreditsInfo,
  InvitationAlertState,
  ModifyApiKey,
  OrgMembership,
  Organisation,
  RejectedInvite,
  UpdateSubscription,
  UserMembership,
} from "data/models";
import { BatchConfig } from "pages/geoscape-batch/models";
import {
  ClipConfiguration,
  GeoJSONFeatureCollection,
} from "pages/geoscape-data/clip/clip-ui/models";
import { makeApiRequestPostToUrl, makeApiRequestToUrl } from "utils/api-client";
import { PlanDetails, roundDownOverageBudget } from "utils/calculations";
import { PaymentMethod } from "./models";

type ReactQueryMutationError = DefaultError;

const getStripePaymentMethodToken = async (
  stripe: Stripe | null,
  stripeElements: StripeElements | null,
) => {
  if (!stripe || !stripeElements) {
    throw new Error("Unable to access stripe. Please contact support.");
  }

  // Use elements.getElement to get a reference to the mounted Element.
  const cardElement: StripeCardElement | null =
    stripeElements.getElement(CardElement);
  if (!cardElement) {
    throw new Error("Unable to access stripe. Please contact support.");
  }

  // Pass the Element directly to other Stripe.js methods:
  const tokenResult = await stripe.createToken(cardElement);

  if (tokenResult.error) {
    throw new Error(tokenResult.error.message);
  }

  return tokenResult;
};

const addPaymentMethod = async (
  stripe: Stripe | null,
  stripeElements: StripeElements | null,
  setEditingCC: any,
): Promise<PaymentMethod> => {
  const tokenResult = await getStripePaymentMethodToken(stripe, stripeElements);
  let paymentDetails: PaymentMethod[];
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/paymentMethod",
      "POST",
      tokenResult.token,
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages[0]);
    }

    setEditingCC(false);

    paymentDetails = responseData.filter(
      (paymentMethod: PaymentMethod) => paymentMethod.active,
    );
  } catch (err) {
    throw new Error(`Unable to add payment method: ${err}`);
  }

  return paymentDetails[0];
};

async function updateOverageLimit(
  newDollarLimit: number,
  planDetails: PlanDetails,
): Promise<CreditsInfo> {
  const newOverageLimit: number = roundDownOverageBudget(
    newDollarLimit,
    planDetails,
  );

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/overage",
      "POST",
      { limitDollars: newOverageLimit },
    );
    const newOverage = await response.json();

    if (response.status !== 200) {
      throw new Error(newOverage.messages.join(". "));
    }

    return newOverage;
  } catch (err) {
    throw new Error(
      `Unable to update overage limit to $${newDollarLimit} : ${err}`,
    );
  }
}

const submitNewOrganisation = async (orgDetails: Organisation) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/organisation",
      "POST",
      orgDetails,
    );
    const newOrg = await response.json();

    if (response.status !== 200) {
      throw new Error(newOrg.messages.join(". "));
    }

    return newOrg;
  } catch (err) {
    throw new Error(
      `Unable to submit new organisation '${orgDetails.name}' : ${err}`,
    );
  }
};

const editOrganisation = async (values: Organisation) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/organisation",
      "PUT",
      values,
    );
    const updatedOrganisation = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedOrganisation.messages.join(". "));
    }

    return updatedOrganisation;
  } catch (err) {
    throw new Error(
      `Unable to update organisation details for '${values.name}' : ${err}`,
    );
  }
};

const deleteOrganisationInvitation = async (
  organisationApigeeDeveloperId: string,
  developerApigeeDeveloperId: string,
) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?orgId=${organisationApigeeDeveloperId}&devId=${encodeURIComponent(
        developerApigeeDeveloperId,
      )}`,
      "DELETE",
    );
    const deletedOrganisationInvitation = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationInvitation.messages.join(". "));
    }

    return deletedOrganisationInvitation;
  } catch (err) {
    throw new Error(
      `Unable to delete organisation invitation for developer '${developerApigeeDeveloperId}' in organisation '${organisationApigeeDeveloperId}' : ${err}`,
    );
  }
};

const deleteOrganisationMember = async (
  organisationId: string,
  userMemberId: string,
) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?organisationId=${organisationId}&userMemberId=${encodeURIComponent(
        userMemberId,
      )}`,
      "DELETE",
    );
    const deletedOrganisationMember = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationMember.messages.join(". "));
    }

    return deletedOrganisationMember;
  } catch (err) {
    throw new Error(
      `Unable to delete organisation member '${userMemberId}' from organisation '${organisationId}' : ${err}`,
    );
  }
};

const submitOrganisationInvitations = async (
  invitations: InvitationItem[],
  organisationId: string,
): Promise<InvitationAlertState[]> => {
  try {
    const responses = await Promise.all(
      invitations.map((invitation) =>
        makeApiRequestPostToUrl(
          `${import.meta.env.VITE_DELIVERY_API_URL}`,
          "/v2/organisation/membership",
          "POST",
          {
            userEmail: invitation.email,
            organisationId: organisationId,
            invitationStatus: "PENDING",
            role: invitation.role,
          },
        ),
      ),
    );

    const alertStateList: InvitationAlertState[] = [];
    for (let i = 0; i < responses.length; i++) {
      const r = responses[i];
      const res = await r.json();
      const invitation = invitations[i];

      let message = res?.messages?.join(". ");
      let severity: AlertSeverity = "info";
      if (r.status !== 200) {
        severity = "error";
      } else {
        message = res.userExisted
          ? `Invitation to ${invitation.email} has been sent. Invited user is already registered with Geoscape.`
          : `Invitation to ${invitation.email} has been sent. Invited user does not exist in Geoscape. They may need to sign up.`;
        severity = "info";
      }
      alertStateList.push({
        message: message,
        severity: severity,
      });
    }

    return alertStateList;
  } catch (err) {
    throw new Error(
      `Unable to submit organisation invitations for organisation '${organisationId}': ${err}`,
    );
  }
};

const editApiKey = async (updatedValues: ModifyApiKey) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys/${encodeURIComponent(updatedValues.name)}`,
      "PUT",
      updatedValues,
    );
    const updatedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedApiKey.messages.join(". "));
    }

    return updatedApiKey;
  } catch (err) {
    throw new Error(
      `Unable to update api key details for api key '${updatedValues.displayName}' : ${err}`,
    );
  }
};

const deleteApiKey = async (apiKeyName: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys/${encodeURIComponent(apiKeyName)}`,
      "DELETE",
    );
    const deletedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedApiKey.messages.join(". "));
    }

    return deletedApiKey;
  } catch (err) {
    throw new Error(`Unable to delete api key '${apiKeyName}' : ${err}`);
  }
};

const deleteBatchJobs = async (jobs: string[]) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/delete`,
      "POST",
      jobs,
    );
    const deletedJobs = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedJobs.messages.join(". "));
    }

    return deletedJobs;
  } catch (err) {
    throw new Error(`Unable to delete '${jobs.length}' batch jobs: ${err}`);
  }
};

const deleteClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config/${clipId}`,
      "DELETE",
    );

    if (response.status !== 200) {
      const responseData = await response.json();
      throw new Error(
        responseData.messages?.join(". ") || "Failed to delete clip",
      );
    }

    return response;
  } catch (err) {
    throw new Error(`Unable to delete clip: ${err}`);
  }
};

const createApiKey = async (apiKeyValues: ModifyApiKey) => {
  try {
    apiKeyValues.displayName = apiKeyValues.name;

    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys`,
      "POST",
      apiKeyValues,
    );
    const createdApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(createdApiKey.messages.join(". "));
    }

    createdApiKey.status = "approved";

    return createdApiKey;
  } catch (err) {
    throw new Error(
      `Unable to create api key '${apiKeyValues.displayName}': ${err}`,
    );
  }
};

const updateSubscription = async (values: UpdateSubscription) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/subscriptions",
      "POST",
      values,
    );
    const updatedSubscription = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedSubscription.messages.join(". "));
    }

    return updatedSubscription;
  } catch (err) {
    throw new Error(`Unable to update subscription: ${err}`);
  }
};

const cancelFutureSubscription = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/nextSubscription",
      "DELETE",
    );
    const deletedFutureSubscription = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedFutureSubscription.messages.join(". "));
    }

    return deletedFutureSubscription;
  } catch (err) {
    throw new Error(`Unable to delete subscription: ${err}`);
  }
};

const undoScheduledCancellation = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/cancelSubscription",
      "DELETE",
    );
    const cancellation = await response.json();

    if (response.status !== 200) {
      throw new Error(cancellation.messages.join(". "));
    }

    return cancellation;
  } catch (err) {
    throw new Error(`Unable to undo cancellation: ${err}`);
  }
};

const scheduleCancellation = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/subscription",
      "DELETE",
    );
    const cancellation = await response.json();

    if (response.status !== 200) {
      throw new Error(cancellation.messages.join(". "));
    }

    return cancellation;
  } catch (err) {
    throw new Error(`Unable to cancel subscription: ${err}`);
  }
};

async function updateUserRoleOrgMembership(
  role: string,
  accountId: string,
  orgId: string,
): Promise<OrgMembership> {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/${orgId}/member/${accountId}/role`,
      "PUT",
      { role },
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to update user role for '${accountId}' to '${role}' : ${err}`,
    );
  }
}

const userRejectInvitation = async (
  organisationId: string,
): Promise<RejectedInvite> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/user/membership/${organisationId}`,
      "DELETE",
      {},
    );
    const newMembership = await response.json();

    if (response.status !== 200) {
      throw new Error(newMembership.messages.join(". "));
    }

    return newMembership;
  } catch (err) {
    throw new Error(
      `Failed to accept invitation for org ${organisationId} - ${err}`,
    );
  }
};

const userAcceptInvitation = async (
  organisationId: string,
): Promise<UserMembership> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/user/membership",
      "POST",
      {
        organisationId: organisationId,
      },
    );
    const newMembership = await response.json();

    if (response.status !== 200) {
      throw new Error(newMembership.messages.join(". "));
    }

    return newMembership;
  } catch (err) {
    throw new Error(
      `Failed to accept invitation for org ${organisationId} - ${err}`,
    );
  }
};

const createBatchConfig = async (batchConfig: BatchConfig) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/config`,
      "POST",
      batchConfig,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(
      `Unable to save batch config '${batchConfig.batchInfo.batchId}' : ${err}`,
    );
  }
};

const quoteBatchJob = async (batchId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/quote`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to quote batch job '${batchId}' : ${err}`);
  }
};

const startBatchJob = async (
  batchId: string,
  name: string,
  totalRows: number,
  estimatedCost: number,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/start`,
      "POST",
      {
        name,
        totalRows,
        estimatedCost,
      },
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to start batch job '${batchId}' : ${err}`);
  }
};

const stopBatchJob = async (batchId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/stop`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to stop batch job '${batchId}' : ${err}`);
  }
};

const createClipConfig = async (clipConfig: ClipConfiguration) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config`,
      "POST",
      clipConfig,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to save clip '${clipConfig.id}' : ${err}`);
  }
};

const quoteClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/quote`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to quote clip '${clipId}' : ${err}`);
  }
};

const extractClip = async (
  clipId: string,
  paymentMethod: "credits" | "invoice",
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/extract`,
      "POST",
      {
        paymentMethod: paymentMethod,
      },
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to submit clip '${clipId}' : ${err}`);
  }
};

const invoiceClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/invoice`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to invoice clip '${clipId}' : ${err}`);
  }
};

const validateCoupon = async (promoCode: string): Promise<Coupon> => {
  const response = await makeApiRequestPostToUrl(
    `${import.meta.env.VITE_DELIVERY_API_URL}`,
    `/coupon/${promoCode}`,
    "POST",
    {},
  );
  const couponResponse = await response.json();

  if (response.status === 400) {
    throw new Error(couponResponse.messages);
  }

  if (response.status !== 200) {
    throw new Error("An error occured applying the coupon");
  }

  return couponResponse;
};

const submitClipGeometryFile = async (
  s3Key: string,
): Promise<GeoJSONFeatureCollection> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/processFileGeometry`,
      "POST",
      {
        s3Key: s3Key,
      },
    );
    const geometryResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(geometryResponse.messages.join(". "));
    }
    return geometryResponse;
  } catch (err) {
    throw new Error(`Unable to submit clip geometry file: ${err}`);
  }
};

const submitBuildingsSample = async (
  requestData: BuildingsSampleRequest,
): Promise<string> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/buildingsSample",
      "POST",
      requestData,
    );

    if (response.status !== 200) {
      const errorData = await response.json();
      throw new Error(
        errorData.messages?.join(". ") ||
          `Request failed with status ${response.status}`,
      );
    }

    const presignedUrl = await response.text();

    // Clean up the URL - remove any surrounding quotes or whitespace
    const cleanedUrl = presignedUrl.replace(/^["']|["']$/g, "").trim();

    return cleanedUrl;
  } catch (err) {
    throw new Error(`Unable to request buildings sample: ${err}`);
  }
};

const updateCognitoRole = async (
  cognitoUser: any,
  role: string,
): Promise<string> => {
  try {
    await Auth.updateUserAttributes(cognitoUser, {
      "custom:role": role,
    });
    return role;
  } catch (err) {
    throw new Error(`Unable to update custom:role: ${err}`);
  }
};

const resendSignUp = async (email: string): Promise<any> => {
  try {
    return await Auth.resendSignUp(email);
  } catch (err) {
    throw new Error(
      `Unable to resend verification for email '${email}': ${err}`,
    );
  }
};

const confirmSignUp = async (username: string, code: string): Promise<any> => {
  try {
    return await Auth.confirmSignUp(username, code);
  } catch (err) {
    throw new Error(
      `Unable to confirm sign up for username '${username}': ${err}`,
    );
  }
};

interface SignInParams {
  email: string;
  password: string;
  termsOfUse?: boolean;
}

const signIn = async (params: SignInParams): Promise<any> => {
  try {
    const cognitoUser = await Auth.signIn(
      params.email.toLowerCase(),
      params.password,
      {
        "custom:dev_t_and_c": String(params.termsOfUse),
      },
    );
    return cognitoUser;
  } catch (err) {
    throw new Error(`Unable to sign in for email '${params.email}': ${err}`);
  }
};

export {
  addPaymentMethod,
  cancelFutureSubscription,
  confirmSignUp,
  createApiKey,
  createBatchConfig,
  createClipConfig,
  deleteApiKey,
  deleteBatchJobs,
  deleteClip,
  deleteOrganisationInvitation,
  deleteOrganisationMember,
  editApiKey,
  editOrganisation,
  extractClip,
  invoiceClip,
  quoteBatchJob,
  quoteClip,
  resendSignUp,
  scheduleCancellation,
  signIn,
  startBatchJob,
  stopBatchJob,
  submitBuildingsSample,
  submitClipGeometryFile,
  submitNewOrganisation,
  submitOrganisationInvitations,
  undoScheduledCancellation,
  updateCognitoRole,
  updateOverageLimit,
  updateSubscription,
  updateUserRoleOrgMembership,
  userAcceptInvitation,
  userRejectInvitation,
  validateCoupon,
};

export type { ReactQueryMutationError };
